const MAX_WIDTH = 1200;
const MAX_HEIGHT = 1200;
const QUALITY = 0.82;

/**
 * Resize an image and convert it to WebP format client-side.
 * Falls back to JPEG if WebP is not supported.
 */
export async function processImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Only resize if exceeds max dimensions
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas not supported"));

      ctx.drawImage(img, 0, 0, width, height);

      // Try WebP first, fallback to JPEG
      const tryFormat = (mime: string, ext: string) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              if (mime === "image/webp") {
                // WebP not supported, fallback to JPEG
                tryFormat("image/jpeg", "jpg");
                return;
              }
              return reject(new Error("Image conversion failed"));
            }
            const baseName = file.name.replace(/\.[^.]+$/, "");
            resolve(new File([blob], `${baseName}.${ext}`, { type: mime }));
          },
          mime,
          QUALITY
        );
      };

      tryFormat("image/webp", "webp");
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
}

/**
 * Process multiple images in parallel.
 */
export async function processImages(files: File[]): Promise<File[]> {
  return Promise.all(files.map(processImage));
}
